/**
 * useBarcodeScanner — global USB keyboard-wedge scanner listener.
 *
 * USB barcode scanners present themselves as HID keyboards and emit each
 * scanned character as a synthetic keypress, followed by a configurable
 * terminator (Enter or Tab). They type **much** faster than a human:
 * inter-character gaps are typically 1–25 ms vs. 80–250 ms for typing.
 *
 * This hook installs a single capture-phase `keydown` listener on `window`
 * and uses three signals to classify a key sequence as a scan:
 *
 *   1. Inter-character interval is consistently below `maxInterCharMs`.
 *   2. The buffer length on terminator is at least `minLength`.
 *   3. The sequence ends with a configured terminator key.
 *
 * On a confirmed scan:
 *   • The terminator keydown is `preventDefault()`-ed (so it does not submit
 *     the focused form or insert a newline).
 *   • If focus was inside a text input/textarea, that field is **cleared of
 *     the scanned characters** (we know exactly how many characters were
 *     buffered, and they were appended to the field's current value).
 *   • `onScan(code)` is invoked with the trimmed barcode string.
 *
 * On any signal of human typing (slow gap, character outside the allowed
 * set, or non-terminator non-character key like ArrowLeft) the buffer is
 * discarded and the keystrokes are left untouched — typing in inputs and
 * keyboard navigation are never affected.
 *
 * Usage:
 *   useBarcodeScanner({
 *     onScan: (code) => addProductByBarcode(code),
 *     enabled: !modalOpen,            // pause while modals own the keyboard
 *   });
 *
 * Notes:
 *   • Defaults are tuned for Honeywell / Symbol / Zebra scanners in their
 *     factory configuration. For very fast / very slow scanners, override
 *     `maxInterCharMs` and `minLength`.
 *   • Avoid mounting this hook in more than one place at the same level —
 *     each instance attaches its own listener. The recommended location is
 *     once near the application root (BillingScreen / AppShell).
 *   • Keystroke source flag (`event.isTrusted`) is **not** used: most
 *     scanners produce trusted events identical to a real keyboard.
 */

import { useCallback, useEffect, useRef } from 'react';

export interface UseBarcodeScannerOptions {
  /** Called with the captured barcode (already trimmed, terminator stripped). */
  onScan: (code: string) => void;
  /** Disable the listener entirely (e.g. while a modal is open). Default: true. */
  enabled?: boolean;
  /**
   * Maximum allowed gap between two scan characters, in ms.
   * Defaults to 50ms — generous for slow scanners, well above human typing.
   */
  maxInterCharMs?: number;
  /**
   * Minimum length of the buffered string to qualify as a scan.
   * Defaults to 4. Keep ≥ 4 to avoid false positives from quick double-taps.
   */
  minLength?: number;
  /**
   * Which keys terminate a scan. Default: ['Enter', 'Tab'].
   */
  terminators?: ReadonlyArray<'Enter' | 'Tab'>;
  /**
   * Optional regex that the buffered code must match for the scan to fire.
   * Use this to constrain to e.g. `/^\d{8,14}$/` for EAN/UPC only.
   * Default: any non-empty printable string.
   */
  pattern?: RegExp;
}

const DEFAULT_TERMINATORS = ['Enter', 'Tab'] as const;

/** Returns true if `key` is a printable single character we want to buffer. */
function isPrintableChar(key: string): boolean {
  // `key` is a single grapheme for printable chars; named keys are multi-char
  // (e.g. "ArrowLeft", "Shift"). Filter to length 1.
  return key.length === 1;
}

export function useBarcodeScanner(options: UseBarcodeScannerOptions): void {
  const {
    onScan,
    enabled = true,
    maxInterCharMs = 50,
    minLength = 4,
    terminators = DEFAULT_TERMINATORS,
    pattern,
  } = options;

  // Latest callback in a ref so we don't re-bind the listener on every render.
  const onScanRef = useRef(onScan);
  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  // Mutable buffer and timing — refs so they survive re-renders without
  // triggering them.
  const bufferRef = useRef<string>('');
  const lastTimeRef = useRef<number>(0);

  const reset = useCallback(() => {
    bufferRef.current = '';
    lastTimeRef.current = 0;
  }, []);

  useEffect(() => {
    if (!enabled) {
      reset();
      return;
    }

    const handler = (event: KeyboardEvent) => {
      // Ignore modified key combos (Ctrl/Meta/Alt). Shift is fine — scanners
      // use it for upper-case characters.
      if (event.ctrlKey || event.metaKey || event.altKey) {
        reset();
        return;
      }

      const now = performance.now();
      const gap = lastTimeRef.current === 0 ? 0 : now - lastTimeRef.current;
      const isTerminator = (terminators as ReadonlyArray<string>).includes(event.key);

      // ── Terminator: decide whether the buffer was a scan ──
      if (isTerminator) {
        const buf = bufferRef.current;
        const longEnough = buf.length >= minLength;
        const fastEnough = lastTimeRef.current === 0 || gap <= maxInterCharMs;
        const matches = !pattern || pattern.test(buf);

        if (longEnough && fastEnough && matches) {
          // It was a scan. Swallow the terminator so it does not submit a
          // form or insert a newline, then notify the consumer.
          event.preventDefault();
          event.stopPropagation();

          // If the scan happened while focus was inside an input/textarea,
          // the characters were also typed into that field. Strip exactly
          // the buffered characters from the end of its value.
          const target = event.target as HTMLElement | null;
          stripFromInputTail(target, buf);

          const code = buf.trim();
          reset();
          if (code.length > 0) {
            try {
              onScanRef.current(code);
            } catch (err) {
              // Never let a consumer error propagate back into the
              // keyboard event loop.
              // eslint-disable-next-line no-console
              console.error('[useBarcodeScanner] onScan threw:', err);
            }
          }
          return;
        }

        // Not a scan — let the terminator do its normal job (form submit,
        // tab traversal). Reset and bail.
        reset();
        return;
      }

      // ── Non-terminator, non-character (Arrow keys, F-keys, Esc) ──
      // These cannot appear inside a scan; treat as human input and reset.
      if (!isPrintableChar(event.key)) {
        reset();
        return;
      }

      // ── Printable character ──
      // If the gap is too large, this is a fresh sequence (or human typing).
      // We start a new buffer either way; only the terminator decides if
      // we treat it as a scan, so this is safe.
      if (lastTimeRef.current !== 0 && gap > maxInterCharMs) {
        bufferRef.current = '';
      }
      bufferRef.current += event.key;
      lastTimeRef.current = now;
    };

    // Capture phase so we see the keydown before page-level handlers
    // (e.g. the catalog's grid-navigation Enter handler).
    window.addEventListener('keydown', handler, true);
    return () => {
      window.removeEventListener('keydown', handler, true);
      reset();
    };
  }, [enabled, maxInterCharMs, minLength, terminators, pattern, reset]);
}

/**
 * If the scan happened while the focused element was a text input or
 * textarea, the buffered characters were also typed into it. Remove exactly
 * those characters from the tail of the field's current value, leaving any
 * pre-existing text intact.
 *
 * Uses the native value setter so React's controlled inputs see the change
 * via their `onChange` handler.
 */
function stripFromInputTail(target: HTMLElement | null, buf: string): void {
  if (!target) return;
  const isInput = target.tagName === 'INPUT';
  const isTextArea = target.tagName === 'TEXTAREA';
  if (!isInput && !isTextArea) return;

  const el = target as HTMLInputElement | HTMLTextAreaElement;
  const current = el.value ?? '';
  if (!current.endsWith(buf)) return; // can happen if user typed in between

  const next = current.slice(0, current.length - buf.length);

  // Use the native setter so React's synthetic event system observes the
  // mutation. Required for controlled inputs (the common case).
  const proto = isInput ? HTMLInputElement.prototype : HTMLTextAreaElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) {
    setter.call(el, next);
  } else {
    el.value = next;
  }
  el.dispatchEvent(new Event('input', { bubbles: true }));
}
